import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { TrendChart } from '../components/TrendChart'
import { buildAnalysis, type ProductContribution } from '../analysis'
import {
  type AdPeriodSummary,
  type AdProductCandidate,
  type AdSearchCandidate,
  type FlowActionItem,
  type P1Snapshot,
  type P2Snapshot,
  type P3ReadySnapshot,
  type P5Snapshot,
  type QuadrantItem,
  type QuadrantKey,
  type RefundRiskItem,
  type RefundStats,
  type RefundWindow,
  useAnalysisExpansionData,
} from '../analysisData'
import { useProductData } from '../data'
import type { ProductRecord } from '../types'
import { integer, money, percent } from '../utils'

const dateRange = (dates: string[]) => dates.length ? `${dates[0]} 至 ${dates[dates.length - 1]}` : '数据不足'
const signedPercent = (value: number | null) => value === null ? '新增' : `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
const signedMoney = (value: number) => `${value >= 0 ? '+' : '-'}${money(Math.abs(value))}`
const signedInteger = (value: number) => `${value >= 0 ? '+' : '-'}${integer(Math.abs(value))}`
const signedPoint = (value: number | null) => value === null ? '—' : `${value >= 0 ? '+' : ''}${value.toFixed(2)}pct`

function ProductIdentity({ item }: { item: { product: ProductRecord } }) {
  return (
    <Link className="analysis-product-link" to={`/product/${item.product.level}/${item.product.id}`}>
      {item.product.image
        ? <img src={item.product.image} alt="" loading="lazy" />
        : <span className="analysis-image-fallback">无图</span>}
      <span><strong>{item.product.name}</strong><small>{item.product.level.toUpperCase()} · {item.product.id}</small></span>
    </Link>
  )
}

function TrafficStructureSection({
  snapshot,
  selectedDate,
  loading,
  error,
}: {
  snapshot: P1Snapshot | null
  selectedDate: string
  loading: boolean
  error: string
}) {
  const ready = snapshot?.status === 'ready' ? snapshot : null
  const leadingDecline = ready?.channels.find((item) => item.visitorDelta < 0)
  return (
    <section className="analysis-section traffic-structure-section">
      <div className="section-heading-row">
        <div>
          <h2>流量结构</h2>
          <p>商智流量来源 · 店铺来源口径，与商品访客不可比</p>
        </div>
        <div className="section-heading-meta">
          <DataFreshnessBadge date={snapshot?.dataThrough} label="流量数据截至" mode="on-demand" />
          {ready && <span>{ready.currentWindow.start} 至 {ready.currentWindow.end} 对比 {ready.previousWindow.start} 至 {ready.previousWindow.end}</span>}
        </div>
      </div>
      {loading && <div className="analysis-inline-state">正在加载流量结构数据…</div>}
      {error && <div className="analysis-inline-state is-warning">{error}，不影响本页其他分析。</div>}
      {!loading && !error && !snapshot && (
        <div className="analysis-inline-state is-warning">当前没有可用的流量结构数据。</div>
      )}
      {snapshot?.status === 'insufficient' && (
        <div className="analysis-inline-state is-warning">
          流量来源数据不足连续双7天，暂不生成环比与执行动作。
        </div>
      )}
      {ready && <>
        {selectedDate !== ready.dataThrough && (
          <div className="analysis-inline-state is-warning">
            本板块使用独立流量数据的最新完整窗口，数据截至 {ready.dataThrough}，不随上方商品业务日期切换。
          </div>
        )}
        <MetricStrip items={[
          {
            label: '来源访客',
            value: integer(ready.totals.current.visitors),
            context: `较前7天 ${signedPercent(ready.totals.visitorChangeRate)}`,
          },
          {
            label: '引入成交金额',
            value: money(ready.totals.current.gmv),
            context: `较前7天 ${signedPercent(ready.totals.gmvChangeRate)}`,
          },
          {
            label: '来源转化率',
            value: ready.totals.current.conversionRate === null
              ? '—'
              : percent(ready.totals.current.conversionRate),
            context: `较前7天 ${signedPoint(ready.totals.conversionRatePoint)}`,
          },
          {
            label: '访客变化',
            value: signedInteger(ready.totals.visitorDelta),
            context: '一级站内＋一级站外，不混加二级',
          },
        ]} />
        <div className="traffic-structure-conclusion">
          {leadingDecline
            ? <p>来源访客减少 {integer(Math.abs(ready.totals.visitorDelta))}，负向变化主要来自<strong>{leadingDecline.channel}</strong>：访客 {signedInteger(leadingDecline.visitorDelta)}，占全部负向来源变化 {percent(leadingDecline.declineContributionRate)}。</p>
            : <p>当前双7天没有访客下降的来源场域。</p>}
        </div>
        <div className="table-responsive">
          <table className="traffic-structure-table">
            <thead>
              <tr>
                <th>流量场域</th>
                <th className="numeric">本期访客</th>
                <th className="numeric">上期访客</th>
                <th className="numeric">访客变化</th>
                <th className="numeric">负向来源占比</th>
                <th className="numeric">本期成交</th>
                <th className="numeric">成交变化</th>
                <th className="numeric">来源转化率</th>
              </tr>
            </thead>
            <tbody>
              {ready.channels.map((item) => (
                <tr key={`${item.scope}-${item.channel}`}>
                  <td><span className="traffic-channel"><strong>{item.channel}</strong><small>{item.scope} · {item.hierarchyDepth === 1 ? '一级' : '二级'}</small></span></td>
                  <td className="numeric">{integer(item.current.visitors)}</td>
                  <td className="numeric">{integer(item.previous.visitors)}</td>
                  <td className={`numeric ${item.visitorDelta >= 0 ? 'analysis-positive' : 'analysis-negative'}`}>{signedInteger(item.visitorDelta)}</td>
                  <td className="numeric">{item.visitorDelta < 0 ? percent(item.declineContributionRate) : '—'}</td>
                  <td className="numeric">{money(item.current.gmv)}</td>
                  <td className={`numeric ${item.gmvDelta >= 0 ? 'analysis-positive' : 'analysis-negative'}`}>{signedMoney(item.gmvDelta)}</td>
                  <td className="numeric">{item.current.conversionRate === null ? '—' : percent(item.current.conversionRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="quadrant-methodology">
          店铺总览只汇总一级站内场域与一级站外渠道；明细按站内二级、站外一级展示。负向来源占比＝单场域访客减少÷全部下降场域访客减少，不代表商品访客贡献。
        </p>
      </>}
    </section>
  )
}

function AdPeriodCard({
  title,
  current,
  previous,
}: {
  title: string
  current: AdPeriodSummary
  previous: AdPeriodSummary
}) {
  const spendDelta = current.spend - previous.spend
  const gmvDelta = current.attributedGmv - previous.attributedGmv
  return (
    <article className="ad-period-card">
      <header><strong>{title}</strong><span>独立报表口径</span></header>
      <div>
        <span>花费<strong>{money(current.spend)}</strong><small className={spendDelta >= 0 ? 'analysis-positive' : 'analysis-negative'}>{signedMoney(spendDelta)}</small></span>
        <span>归因成交<strong>{money(current.attributedGmv)}</strong><small className={gmvDelta >= 0 ? 'analysis-positive' : 'analysis-negative'}>{signedMoney(gmvDelta)}</small></span>
        <span>ROI<strong>{current.roi === null ? '—' : current.roi.toFixed(2)}</strong><small>上期 {previous.roi === null ? '—' : previous.roi.toFixed(2)}</small></span>
      </div>
    </article>
  )
}

function AdSkuCell({
  item,
  product,
}: {
  item: { skuId: string; skuName: string }
  product?: ProductRecord
}) {
  return product
    ? <ProductIdentity item={{ product }} />
    : <span className="ad-object-cell"><strong>{item.skuName}</strong><small>SKU · {item.skuId}</small></span>
}

function SearchCandidateTable({
  items,
}: {
  items: AdSearchCandidate[]
}) {
  return (
    <div className="analysis-table-block ad-candidate-block">
      <div className="ad-table-heading"><h3>否词复核候选</h3><span>按无归因成交花费降序 · 展示前10组</span></div>
      <div className="table-responsive">
        <table className="ad-diagnostic-table search-candidate-table">
          <thead><tr><th>搜索词</th><th>计划 / 推广SKU</th><th className="numeric">花费</th><th className="numeric">点击</th></tr></thead>
          <tbody>{items.slice(0, 10).map((item) => (
            <tr key={`${item.campaignId}-${item.unitId}-${item.skuId}-${item.searchTerm}`}>
              <td><span className="ad-object-cell"><strong>{item.searchTerm}</strong><small>单元 {item.unitId}</small></span></td>
              <td>
                <span className="ad-object-cell"><strong>{item.campaignName}</strong><small>{item.skuName} · {item.skuId}</small></span>
              </td>
              <td className="numeric">{money(item.spend)}</td>
              <td className="numeric">{integer(item.clicks)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {!items.length && <div className="empty-state">当前15天没有无归因成交的搜索词候选。</div>}
    </div>
  )
}

function ProductCandidateTable({
  title,
  note,
  items,
  productsByKey,
  kind,
}: {
  title: string
  note: string
  items: AdProductCandidate[]
  productsByKey: Map<string, ProductRecord>
  kind: 'zero' | 'low'
}) {
  return (
    <div className="analysis-table-block ad-candidate-block">
      <div className="ad-table-heading"><h3>{title}</h3><span>{note}</span></div>
      {items.length ? <div className="table-responsive">
        <table className="ad-diagnostic-table product-candidate-table">
          <thead><tr><th>推广SKU</th><th className="numeric">花费</th><th className="numeric">归因成交</th><th className="numeric">实际ROI</th><th className="numeric">盈亏ROI</th><th className="numeric">含返点利润率</th></tr></thead>
          <tbody>{items.slice(0, 10).map((item) => (
            <tr key={`${kind}-${item.skuId}`}>
              <td><AdSkuCell item={item} product={productsByKey.get(`sku:${item.skuId}`)} /></td>
              <td className="numeric">{money(item.spend)}</td>
              <td className="numeric">{money(item.attributedGmv)}</td>
              <td className="numeric">{item.actualRoi === null ? '—' : item.actualRoi.toFixed(2)}</td>
              <td className="numeric">{item.breakEvenRoi === null ? '—' : item.breakEvenRoi.toFixed(2)}</td>
              <td className="numeric">{item.profitWithRebateMargin === null ? '—' : percent(item.profitWithRebateMargin * 100)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div> : <div className="empty-state">
        {kind === 'zero'
          ? '当前15天没有花费超过100元且归因成交为0的推广SKU。'
          : '当前15天没有可计算且低于盈亏ROI的推广SKU。'}
      </div>}
    </div>
  )
}

function AdvertisingEfficiencySection({
  snapshot,
  productsByKey,
  loading,
  error,
}: {
  snapshot: P2Snapshot | null
  productsByKey: Map<string, ProductRecord>
  loading: boolean
  error: string
}) {
  const topCampaign = snapshot?.searchTerms.campaignGroups[0]
  return (
    <section className="analysis-section advertising-diagnostic-section">
      <div className="section-heading-row">
        <div>
          <h2>广告效率（固定15天）</h2>
          <p>15天点击归因 · 不含赠品成交订单 · 搜索词与商品SKU报表独立分析</p>
        </div>
        <div className="section-heading-meta">
          <DataFreshnessBadge date={snapshot?.currentWindow.end} label="广告效果截至" mode="on-demand" />
          {snapshot && <span>{snapshot.currentWindow.start} 至 {snapshot.currentWindow.end} 对比 {snapshot.previousWindow.start} 至 {snapshot.previousWindow.end}</span>}
        </div>
      </div>
      {loading && <div className="analysis-inline-state">正在加载广告15天诊断…</div>}
      {error && <div className="analysis-inline-state is-warning">{error}，不影响本页其他分析。</div>}
      {!loading && !error && !snapshot && <div className="analysis-inline-state is-warning">当前没有可用的固定15天广告报表。</div>}
      {snapshot && <>
        <div className="analysis-inline-state is-info">
          本板块不随上方业务日期切换；两份效果报表口径不同，彼此不混加，也不与广告费用页财务口径对账。
        </div>
        <div className="ad-period-grid">
          <AdPeriodCard title="搜索词效果报表" current={snapshot.searchTerms.current} previous={snapshot.searchTerms.previous} />
          <AdPeriodCard title="商品SKU效果报表" current={snapshot.products.current} previous={snapshot.products.previous} />
        </div>
        <div className="ad-diagnostic-conclusion">
          <p>
            搜索词细分后有 <strong>{integer(snapshot.searchTerms.zeroCandidateCount)}组</strong> 花费后尚无归因成交，
            合计 <strong>{money(snapshot.searchTerms.zeroAttributionSpend)}</strong>、{integer(snapshot.searchTerms.zeroAttributionClicks)}次点击。
          </p>
          {topCampaign && <p>
            候选花费最高的计划是<strong>{topCampaign.campaignName}</strong>：
            {money(topCampaign.spend)}，共{integer(topCampaign.candidateCount)}组；需先区分品牌防守、探索和低效词。
          </p>}
          <p>
            SKU盈亏线覆盖 {snapshot.products.marginCoverage.eligibleSkuCount}/{snapshot.products.marginCoverage.adSkuCount} 个广告SKU；
            成本口径使用 {snapshot.products.marginCoverage.sourceWindowStart} 至 {snapshot.products.marginCoverage.sourceWindowEnd} 当前商品含返点利润率。
          </p>
        </div>
        <SearchCandidateTable items={snapshot.searchTerms.candidates} />
        <ProductCandidateTable
          title="高花费无归因SKU"
          note="花费＞100元且归因成交为0"
          items={snapshot.products.zeroAttribution}
          productsByKey={productsByKey}
          kind="zero"
        />
        <ProductCandidateTable
          title="低于盈亏ROI的推广SKU"
          note="仅展示成本完整且含返点利润率＞0的SKU · 展示前10款"
          items={snapshot.products.lowEfficiency}
          productsByKey={productsByKey}
          kind="low"
        />
        <p className="quadrant-methodology">
          盈亏ROI＝1÷当前商品窗口含返点利润率。成本缺失、覆盖不完整或利润率不大于0时不计算；候选只用于复核，任何否词、出价、预算或暂停操作均待Gavin当次确认。
        </p>
      </>}
    </section>
  )
}

function ContributionTable({ title, items }: { title: string, items: ProductContribution[] }) {
  return (
    <div className="analysis-table-block">
      <h3>{title}</h3>
      <div className="table-responsive">
        <table className="contribution-table">
          <thead><tr><th>商品</th><th className="numeric">成交变化</th><th className="numeric">主要驱动</th></tr></thead>
          <tbody>{items.map((item) => (
            <tr key={item.product.id}>
              <td><ProductIdentity item={item} /></td>
              <td className={`numeric ${item.delta >= 0 ? 'analysis-positive' : 'analysis-negative'}`}>{signedMoney(item.delta)}</td>
              <td className="numeric">{item.driver}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  )
}

const quadrantDetails: Record<QuadrantKey, { title: string, action: string, className: string }> = {
  highTrafficHighConversion: {
    title: '高流量 · 高转化',
    action: '稳定维护，留意利润和库存',
    className: 'is-strong',
  },
  highTrafficLowConversion: {
    title: '高流量 · 低转化',
    action: '优先检查详情、价格和评价',
    className: 'is-conversion-risk',
  },
  lowTrafficHighConversion: {
    title: '低流量 · 高转化',
    action: '值得验证精准增流',
    className: 'is-traffic-opportunity',
  },
  lowTrafficLowConversion: {
    title: '低流量 · 低转化',
    action: '进入观察池，不直接建议下架',
    className: 'is-observation',
  },
}

function QuadrantPanel({
  quadrant,
  items,
  productsById,
}: {
  quadrant: QuadrantKey
  items: QuadrantItem[]
  productsById: Map<string, ProductRecord>
}) {
  const detail = quadrantDetails[quadrant]
  return (
    <article className={`quadrant-panel ${detail.className}`}>
      <header>
        <div><h3>{detail.title}</h3><p>{detail.action}</p></div>
        <strong>{items.length}款</strong>
      </header>
      <div className="quadrant-product-list">
        {items.slice(0, 6).map((item) => {
          const product = productsById.get(item.spuId)
          if (!product) return null
          return (
            <div className="quadrant-product-row" key={item.spuId}>
              <ProductIdentity item={{ product }} />
              <span>
                <strong>{integer(item.visitors)}访客</strong>
                <small>转化 {percent(item.conversionRate)} · 成交 {money(item.gmv)}</small>
              </span>
            </div>
          )
        })}
        {!items.length && <div className="empty-state">当前没有符合条件的SPU。</div>}
        {items.length > 6 && <small className="quadrant-more">另有 {items.length - 6} 款，已计入本象限</small>}
      </div>
    </article>
  )
}

function EfficiencyQuadrant({
  snapshot,
  productsById,
  loading,
  error,
}: {
  snapshot: P3ReadySnapshot | null
  productsById: Map<string, ProductRecord>
  loading: boolean
  error: string
}) {
  return (
    <section className="analysis-section efficiency-quadrant-section">
      <div className="section-heading-row">
        <div>
          <h2>单品效率四象限</h2>
          <p>最近7天SPU访客 × 成交转化率；累计访客少于30的商品不参与阈值计算</p>
        </div>
        {snapshot && <span>{snapshot.windowStart} 至 {snapshot.windowEnd}</span>}
      </div>
      {loading && <div className="analysis-inline-state">正在加载四象限数据…</div>}
      {error && <div className="analysis-inline-state is-warning">{error}，不影响本页其他分析。</div>}
      {!loading && !error && !snapshot && (
        <div className="analysis-inline-state is-warning">当前日期不足连续7天，暂不生成四象限。</div>
      )}
      {snapshot && <>
        <div className="quadrant-thresholds">
          <span>流量中位数 <strong>{integer(snapshot.medianVisitors)}访客</strong></span>
          <span>转化中位数 <strong>{percent(snapshot.medianConversionRate)}</strong></span>
          <span>纳入 <strong>{snapshot.eligibleSpuCount}款</strong></span>
          <span>低样本排除 <strong>{snapshot.excludedLowVisitorCount}款</strong></span>
        </div>
        <div className="quadrant-grid">
          {(Object.keys(quadrantDetails) as QuadrantKey[]).map((quadrant) => (
            <QuadrantPanel
              key={quadrant}
              quadrant={quadrant}
              items={snapshot.quadrants[quadrant]}
              productsById={productsById}
            />
          ))}
        </div>
        <p className="quadrant-methodology">
          等于中位数归入“高”；转化率按7天成交客户数÷访客数重算。低流量低转化仅作观察，不直接作为下架依据。
        </p>
      </>}
    </section>
  )
}

const refundShare = (stats: RefundStats | null) => {
  if (!stats) return '缺失'
  if (stats.refundAmountShare === null) return stats.refundAmount > 0 ? '不可计算' : '—'
  return percent(stats.refundAmountShare * 100)
}

const refundWindowLabel = (item: RefundRiskItem) => {
  if (item.isSevenDayAnomaly && item.isThirtyDayAnomaly) return '7天、30天'
  if (item.isSevenDayAnomaly) return '最近7天'
  return '最近30天'
}

function RefundRiskTable({
  title,
  items,
  productsByKey,
}: {
  title: string
  items: RefundRiskItem[]
  productsByKey: Map<string, ProductRecord>
}) {
  return (
    <div className="analysis-table-block refund-risk-table-block">
      <h3>{title}</h3>
      <div className="table-responsive">
        <table className="refund-risk-table">
          <thead>
            <tr>
              <th>商品</th>
              <th className="numeric">近7天取消退款</th>
              <th className="numeric">近7天占比</th>
              <th className="numeric">近30天取消退款</th>
              <th className="numeric">近30天占比</th>
              <th>异常窗口</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? items.map((item) => {
              const product = productsByKey.get(`${item.level}:${item.productId}`)
              return (
                <tr key={`${item.level}-${item.productId}`}>
                  <td>{product
                    ? <ProductIdentity item={{ product }} />
                    : <span>{item.name}<small>{item.level.toUpperCase()} · {item.productId}</small></span>}
                  </td>
                  <td className="numeric">{item.sevenDay ? money(item.sevenDay.refundAmount) : '缺失'}</td>
                  <td className="numeric">{refundShare(item.sevenDay)}</td>
                  <td className="numeric">{item.thirtyDay ? money(item.thirtyDay.refundAmount) : '缺失'}</td>
                  <td className="numeric">{refundShare(item.thirtyDay)}</td>
                  <td><span className="refund-window-label">{refundWindowLabel(item)}</span></td>
                </tr>
              )
            }) : (
              <tr><td colSpan={6}><div className="empty-state">当前窗口没有达到异常规则的{title}。</div></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const readyRefundWindow = (window: RefundWindow) => window.status === 'ready' ? window : null

function RefundRiskSection({
  snapshot,
  productsByKey,
  loading,
  error,
}: {
  snapshot: P5Snapshot | null
  productsByKey: Map<string, ProductRecord>
  loading: boolean
  error: string
}) {
  const sevenDay = snapshot ? readyRefundWindow(snapshot.sevenDay) : null
  const thirtyDay = snapshot ? readyRefundWindow(snapshot.thirtyDay) : null
  const trend = snapshot?.dailyTrend.map((item) => ({ date: item.date, value: item.refundAmount })) || []
  return (
    <section className="analysis-section refund-risk-section">
      <div className="section-heading-row">
        <div>
          <h2>取消退款风险</h2>
          <p>退款额占比＝取消退款金额÷同期成交金额，包含跨期退款，不等于退货率</p>
        </div>
        {thirtyDay && <span>{thirtyDay.windowStart} 至 {thirtyDay.windowEnd}</span>}
      </div>
      {loading && <div className="analysis-inline-state">正在加载退款风险数据…</div>}
      {error && <div className="analysis-inline-state is-warning">{error}，不影响本页其他分析。</div>}
      {!loading && !error && !snapshot && (
        <div className="analysis-inline-state is-warning">当前日期没有可用的退款风险数据。</div>
      )}
      {snapshot && <>
        {!sevenDay && !thirtyDay && (
          <div className="analysis-inline-state is-warning">
            当前日期之前不足完整7天和30天，暂不生成退款风险结论。
          </div>
        )}
        <MetricStrip items={[
          {
            label: '近7天取消退款',
            value: sevenDay ? money(sevenDay.levels.spu.refundAmount) : '缺失',
            context: sevenDay ? `成交 ${money(sevenDay.levels.spu.gmv)}` : '窗口不足7个自然日',
          },
          {
            label: '近7天退款额占比',
            value: sevenDay?.levels.spu.refundAmountShare === null || !sevenDay
              ? '不可计算'
              : percent(sevenDay.levels.spu.refundAmountShare * 100),
            context: sevenDay ? `异常 SPU ${sevenDay.levels.spu.anomalyCount} · SKU ${sevenDay.levels.sku.anomalyCount}` : '窗口不足',
          },
          {
            label: '近30天取消退款',
            value: thirtyDay ? money(thirtyDay.levels.spu.refundAmount) : '缺失',
            context: thirtyDay ? `成交 ${money(thirtyDay.levels.spu.gmv)}` : '窗口不足30个自然日',
          },
          {
            label: '近30天退款额占比',
            value: thirtyDay?.levels.spu.refundAmountShare === null || !thirtyDay
              ? '不可计算'
              : percent(thirtyDay.levels.spu.refundAmountShare * 100),
            context: thirtyDay ? `异常 SPU ${thirtyDay.levels.spu.anomalyCount} · SKU ${thirtyDay.levels.sku.anomalyCount}` : '窗口不足',
          },
        ]} />
        {thirtyDay && trend.length === 30 && (
          <div className="refund-trend-block">
            <div className="section-heading-row">
              <h3>近30天店铺取消退款金额趋势</h3>
              <span>SPU汇总 · 金额（元）</span>
            </div>
            <TrendChart
              data={trend}
              valueFormatter={(value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0)}
              valueLabel="取消退款金额"
              ariaLabel="近30天店铺取消退款金额趋势"
            />
          </div>
        )}
        <div className="refund-risk-tables">
          <RefundRiskTable title="异常SPU" items={snapshot.spuRisks} productsByKey={productsByKey} />
          <RefundRiskTable title="异常SKU" items={snapshot.skuRisks} productsByKey={productsByKey} />
        </div>
        <p className="quadrant-methodology">
          异常规则：占比高于同层级店铺加权基准2倍且取消退款金额不少于500元。成交金额为0时占比不可计算，只保留金额证据；不计算“净成交”。
        </p>
      </>}
    </section>
  )
}

function evidenceFor(item: ProductContribution & { reason: 'decline' | 'conversion' | 'refund' }) {
  if (item.reason === 'refund') return `近7天取消退款 ${money(item.current.cancelRefundAmount)}；成交 ${money(item.current.gmv)}`
  if (item.reason === 'conversion') return `近7天访客 ${integer(item.current.productVisitors)}；转化率 ${percent(item.current.conversionRate)}，低于活跃SPU中位水平`
  return `近7天成交减少 ${money(Math.abs(item.delta))}；${item.driver}`
}

function actionFor(item: ProductContribution & { reason: 'decline' | 'conversion' | 'refund' }) {
  if (item.reason === 'refund') return '复核退款商品、售后原因与批次；涉及商品或客服调整时待Gavin确认'
  if (item.reason === 'conversion') return '核对详情卖点、评价和优惠展示；线上页面调整待Gavin确认'
  if (item.driverKey === 'traffic') return '核对搜索/推荐来源与主图点击；投放或主图调整待Gavin确认'
  if (item.driverKey === 'conversion') return '核对价格、详情和评价变化；线上改动待Gavin确认'
  return '核对组合销售、规格结构与活动承接；促销调整待Gavin确认'
}

function indicatorsFor(item: ProductContribution & { reason: 'decline' | 'conversion' | 'refund' }) {
  if (item.reason === 'refund') return '退款金额、退款件数、成交金额'
  if (item.driverKey === 'traffic') return '访客、搜索点击、成交金额'
  if (item.driverKey === 'conversion') return '访客、成交客户、转化率'
  return '客单价、成交件数、成交金额'
}

export type AnalysisPageMode = 'overview' | 'business' | 'traffic' | 'advertising' | 'refunds'

const ANALYSIS_PAGE_ITEMS: Array<{
  mode: AnalysisPageMode
  to: string
  label: string
  description: string
}> = [
  {
    mode: 'overview',
    to: '/analysis',
    label: '总览',
    description: '跨经营、流量、广告和退款的风险摘要与当前优先事项',
  },
  {
    mode: 'business',
    to: '/analysis/business',
    label: '经营变化',
    description: '成交为什么增长或下降，哪些商品贡献了变化',
  },
  {
    mode: 'traffic',
    to: '/analysis/traffic',
    label: '流量与转化',
    description: '流量从哪里变化，哪些商品承接效率需要改善',
  },
  {
    mode: 'advertising',
    to: '/analysis/advertising',
    label: '广告诊断',
    description: '固定15天广告效率、无归因对象与盈亏ROI复核',
  },
  {
    mode: 'refunds',
    to: '/analysis/refunds',
    label: '退款与售后',
    description: '退款金额、退款额占比与异常商品复核',
  },
]

interface PriorityItem {
  key: string
  domain: string
  title: string
  evidence: string
  source: string
  action: string
  indicators: string
  to: string
  product?: ProductRecord
}

export function AnalysisPage({ mode = 'overview' }: { mode?: AnalysisPageMode }) {
  const { data, selectedDate } = useProductData()
  const analysisExpansion = useAnalysisExpansionData()
  const analysis = useMemo(() => data ? buildAnalysis(data, selectedDate) : null, [data, selectedDate])
  const productsById = useMemo(
    () => new Map(
      (data?.products || [])
        .filter((product) => product.level === 'spu')
        .map((product) => [product.id, product]),
    ),
    [data],
  )
  const productsByKey = useMemo(
    () => new Map((data?.products || []).map((product) => [`${product.level}:${product.id}`, product])),
    [data],
  )
  const p1Snapshot = analysisExpansion.data?.p1.latest || null
  const p2Snapshot = analysisExpansion.data?.p2.latest || null
  const p3Snapshot = useMemo(() => {
    const snapshot = analysisExpansion.data?.p3.byDate[selectedDate]
    return snapshot?.status === 'ready' ? snapshot : null
  }, [analysisExpansion.data, selectedDate])
  const p5Snapshot = useMemo(
    () => analysisExpansion.data?.p5.byDate[selectedDate] || null,
    [analysisExpansion.data, selectedDate],
  )
  if (!data || !analysis) return null

  const event = data.businessEvents?.slice().sort((a, b) => b.startDate.localeCompare(a.startDate))[0]
  const eventStatus = event
    ? data.meta.maxDate < event.startDate
      ? `当前数据截至${data.meta.maxDate}，活动效果待更新后判断`
      : data.meta.maxDate < event.endDate
        ? `当前数据已进入活动期，完整效果需等待${event.endDate}的T+1数据`
        : '活动期数据已完整，可结合前期进行比较'
    : ''
  const totalDelta = analysis.current.gmv - analysis.previous.gmv
  const maxEffect = Math.max(...analysis.effects.map((effect) => Math.abs(effect.value)), 1)
  const mainEffect = analysis.effects.slice().sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0]
  const direction = totalDelta >= 0 ? '增加' : '减少'
  const periodReady = analysis.currentDates.length === 7 && analysis.previousDates.length === 7
  const existingAttentionIds = new Set(analysis.attention.map((item) => item.product.id))
  const quadrantActions = (p3Snapshot?.actionItems || [])
    .filter((item) => !existingAttentionIds.has(item.spuId))
    .flatMap((item) => {
      const product = productsById.get(item.spuId)
      return product ? [{ ...item, product }] : []
    })
  const refundActions = (p5Snapshot?.actionItems || []).flatMap((item) => {
    const product = productsByKey.get(`spu:${item.productId}`)
    return product ? [{ ...item, product }] : []
  })
  const trafficActions: FlowActionItem[] = p1Snapshot?.status === 'ready'
    ? p1Snapshot.actionItems
    : []
  const advertisingActions = p2Snapshot?.actionItems || []
  const pageDefinition = ANALYSIS_PAGE_ITEMS.find((item) => item.mode === mode) || ANALYSIS_PAGE_ITEMS[0]
  const priorityItems: PriorityItem[] = []
  const businessPriority = analysis.attention[0]
  if (businessPriority) {
    priorityItems.push({
      key: `business-${businessPriority.product.id}`,
      domain: '经营变化',
      title: businessPriority.product.name,
      evidence: evidenceFor(businessPriority),
      source: `商智商品明细 · ${dateRange(analysis.currentDates)}`,
      action: actionFor(businessPriority),
      indicators: indicatorsFor(businessPriority),
      to: `/product/${businessPriority.product.level}/${businessPriority.product.id}`,
      product: businessPriority.product,
    })
  }
  const trafficPriority = trafficActions[0]
  if (trafficPriority) {
    priorityItems.push({
      key: `traffic-${trafficPriority.scope}-${trafficPriority.channel}`,
      domain: '流量与转化',
      title: trafficPriority.channel,
      evidence: `访客 ${integer(trafficPriority.previous.visitors)} → ${integer(trafficPriority.current.visitors)}（${signedInteger(trafficPriority.visitorDelta)}）`,
      source: p1Snapshot?.status === 'ready'
        ? `商智流量来源 · ${p1Snapshot.currentWindow.start} 至 ${p1Snapshot.currentWindow.end}`
        : '商智流量来源 · 最新可用窗口',
      action: trafficPriority.action,
      indicators: '来源访客、引入成交金额、来源转化率',
      to: '/analysis/traffic',
    })
  }
  const advertisingPriority = advertisingActions[0]
  if (advertisingPriority) {
    const product = advertisingPriority.kind === 'sku'
      ? productsByKey.get(`sku:${advertisingPriority.objectId}`)
      : undefined
    priorityItems.push({
      key: `advertising-${advertisingPriority.kind}-${advertisingPriority.objectId}`,
      domain: '广告诊断',
      title: advertisingPriority.objectName,
      evidence: advertisingPriority.evidence,
      source: p2Snapshot
        ? `京准通搜索词/商品效果 · ${p2Snapshot.currentWindow.start} 至 ${p2Snapshot.currentWindow.end} · 15天点击归因`
        : '京准通搜索词/商品效果 · 固定15天点击归因',
      action: advertisingPriority.action,
      indicators: advertisingPriority.indicators,
      to: product ? `/product/sku/${product.id}` : '/analysis/advertising',
      product,
    })
  }
  const refundPriority = refundActions[0]
  if (refundPriority) {
    const stats = refundPriority.isThirtyDayAnomaly ? refundPriority.thirtyDay : refundPriority.sevenDay
    priorityItems.push({
      key: `refund-${refundPriority.productId}`,
      domain: '退款与售后',
      title: refundPriority.product.name,
      evidence: `${refundWindowLabel(refundPriority)}取消退款 ${stats ? money(stats.refundAmount) : '缺失'}，退款额占比 ${refundShare(stats)}`,
      source: `商智商品明细 · 截至 ${selectedDate} · 取消退款金额`,
      action: refundPriority.action,
      indicators: '取消退款金额、退款额占比、退款原因分布',
      to: `/product/${refundPriority.product.level}/${refundPriority.product.id}`,
      product: refundPriority.product,
    })
  }
  const quadrantPriority = quadrantActions[0]
  if (quadrantPriority && priorityItems.length < 5) {
    priorityItems.push({
      key: `quadrant-${quadrantPriority.spuId}`,
      domain: '流量与转化',
      title: quadrantPriority.product.name,
      evidence: `近7天访客 ${integer(quadrantPriority.visitors)}，转化率 ${percent(quadrantPriority.conversionRate)}`,
      source: `商智商品明细 · ${dateRange(analysis.currentDates)}`,
      action: quadrantPriority.action,
      indicators: quadrantPriority.actionType === 'conversion' ? '访客、成交客户、转化率' : '新增访客、转化率、含返点利润',
      to: `/product/spu/${quadrantPriority.product.id}`,
      product: quadrantPriority.product,
    })
  }
  const pageTiming = mode === 'traffic'
    ? p1Snapshot?.status === 'ready'
      ? `${p1Snapshot.currentWindow.start} 至 ${p1Snapshot.currentWindow.end} 对比 ${p1Snapshot.previousWindow.start} 至 ${p1Snapshot.previousWindow.end} · 流量数据截至 ${p1Snapshot.dataThrough}`
      : '流量来源数据尚不足完整双7天'
    : mode === 'advertising'
      ? p2Snapshot
        ? `${p2Snapshot.currentWindow.start} 至 ${p2Snapshot.currentWindow.end} 对比 ${p2Snapshot.previousWindow.start} 至 ${p2Snapshot.previousWindow.end} · 固定15天点击归因`
        : '固定15天广告窗口暂不可用'
      : mode === 'overview'
        ? `商品数据截至 ${selectedDate} · 流量与广告使用各自最新完整窗口`
        : mode === 'refunds'
          ? `商品数据截至 ${selectedDate} · 近7天与30天取消退款窗口`
          : `${selectedDate} · ${periodReady ? '最近7天对比前7天' : '可用日期不足完整双7天'}`

  return (
    <div className="analysis-page page-stack">
      <header className="page-title-row">
        <div>
          <h1>机会与风险</h1>
          <p>
            {pageTiming} · {mode === 'overview'
              ? pageDefinition.description
              : `当前查看：${pageDefinition.label} · ${pageDefinition.description}`}
          </p>
          <div className="freshness-group analysis-freshness-group">
            {(mode === 'overview' || mode === 'business' || mode === 'refunds') && (
              <DataFreshnessBadge date={data.meta.maxDate} label="商品数据截至" />
            )}
            {(mode === 'overview' || mode === 'traffic') && (
              <DataFreshnessBadge date={p1Snapshot?.dataThrough} label="流量数据截至" mode="on-demand" />
            )}
            {(mode === 'overview' || mode === 'advertising') && (
              <DataFreshnessBadge date={p2Snapshot?.currentWindow.end} label="广告效果截至" mode="on-demand" />
            )}
          </div>
        </div>
        <div className="analysis-page-actions">
          {mode !== 'overview' && <Link className="text-link" to="/analysis">综合总览</Link>}
          <Link className="text-link" to="/profit">查看利润中心</Link>
        </div>
      </header>

      <section className="analysis-domain-grid" aria-label="机会与风险专题切换">
        <Link
          className={`analysis-domain-card is-business ${mode === 'business' ? 'is-active' : ''}`.trim()}
          to="/analysis/business"
          aria-current={mode === 'business' ? 'page' : undefined}
        >
          <span>经营变化</span>
          <strong className={totalDelta >= 0 ? 'analysis-positive' : 'analysis-negative'}>{signedMoney(totalDelta)}</strong>
          <small>主要影响：{mainEffect.label} {signedMoney(mainEffect.value)}</small>
          <em>{mode === 'business' ? '当前查看' : '查看成交拆解与商品贡献 →'}</em>
        </Link>
        <Link
          className={`analysis-domain-card is-traffic ${mode === 'traffic' ? 'is-active' : ''}`.trim()}
          to="/analysis/traffic"
          aria-current={mode === 'traffic' ? 'page' : undefined}
        >
          <span>流量与转化</span>
          <strong>{trafficActions.length + quadrantActions.length}项</strong>
          <small>来源异常 {trafficActions.length} · 商品效率 {quadrantActions.length}</small>
          <em>{mode === 'traffic' ? '当前查看' : '查看来源变化与承接效率 →'}</em>
        </Link>
        <Link
          className={`analysis-domain-card is-advertising ${mode === 'advertising' ? 'is-active' : ''}`.trim()}
          to="/analysis/advertising"
          aria-current={mode === 'advertising' ? 'page' : undefined}
        >
          <span>广告诊断</span>
          <strong>{advertisingActions.length}项</strong>
          <small>无归因候选 {integer(p2Snapshot?.searchTerms.zeroCandidateCount || 0)} 组</small>
          <em>{mode === 'advertising' ? '当前查看' : '查看固定15天广告效率 →'}</em>
        </Link>
        <Link
          className={`analysis-domain-card is-refunds ${mode === 'refunds' ? 'is-active' : ''}`.trim()}
          to="/analysis/refunds"
          aria-current={mode === 'refunds' ? 'page' : undefined}
        >
          <span>退款与售后</span>
          <strong>{refundActions.length}项</strong>
          <small>近7天 / 30天退款异常对象</small>
          <em>{mode === 'refunds' ? '当前查看' : '查看退款额占比与异常商品 →'}</em>
        </Link>
      </section>

      <div key={mode} className="analysis-topic-content">
      {mode === 'overview' && <>
        <section className="analysis-section analysis-priority-section">
          <div className="section-heading-row">
            <div>
              <h2>当前优先事项</h2>
              <p>跨四类诊断各取最高优先级，最多展示5项</p>
            </div>
            <span>证据 → 动作 → 指标 → 复盘</span>
          </div>
          {priorityItems.length ? <div className="table-responsive">
            <table className="attention-table analysis-priority-table">
              <thead><tr><th>领域</th><th>对象</th><th>证据</th><th>建议动作</th><th>观察指标</th><th>复盘</th></tr></thead>
              <tbody>{priorityItems.slice(0, 5).map((item) => (
                <tr key={item.key}>
                  <td>
                    <Link
                      className="analysis-domain-tag"
                      to={ANALYSIS_PAGE_ITEMS.find((page) => page.label === item.domain)?.to || item.to}
                    >
                      {item.domain}
                    </Link>
                  </td>
                  <td>{item.product
                    ? <ProductIdentity item={{ product: item.product }} />
                    : <Link className="analysis-object-link" to={item.to}>{item.title}</Link>}
                  </td>
                  <td>
                    <span className="analysis-evidence-cell">
                      <span>{item.evidence}</span>
                      <small>{item.source}</small>
                    </span>
                  </td>
                  <td>{item.action}</td>
                  <td>{item.indicators}</td>
                  <td>3天初看<br />7天复盘</td>
                </tr>
              ))}</tbody>
            </table>
          </div> : <div className="empty-state">当前窗口没有需要优先处理的异常事项。</div>}
        </section>

        <footer className="methodology-note">
          总览只保留跨域摘要和最多5项优先事项；详细证据进入对应专题页。利润、成本覆盖和利润风险统一在利润中心查看。
        </footer>
      </>}

      {mode === 'business' && event && <section className="event-band" aria-label="活动说明">
        <strong>活动说明：{event.startDate} 至 {event.endDate} {event.title}</strong>
        <span>{event.description}</span>
        <span>{eventStatus}</span>
      </section>}

      {mode === 'business' && <section className="analysis-section analysis-kpis">
        <MetricStrip items={[
          { label: '近7天成交金额', value: money(analysis.current.gmv), context: `较前7天 ${signedPercent(analysis.gmvRate)}` },
          { label: '商品访客', value: integer(analysis.current.productVisitors), context: `较前7天 ${signedPercent(analysis.visitorRate)}` },
          { label: '成交转化率', value: percent(analysis.current.conversionRate), context: `较前7天 ${analysis.conversionPoint >= 0 ? '+' : ''}${analysis.conversionPoint.toFixed(2)}pct` },
          { label: '客单价', value: money(analysis.current.averageOrderValue), context: `较前7天 ${signedPercent(analysis.averageOrderValueRate)}` },
        ]} />
      </section>}

      {mode === 'business' && <section className="analysis-section decomposition-section">
        <div className="section-heading-row"><h2>成交变化拆解</h2><span>{dateRange(analysis.currentDates)} 对比 {dateRange(analysis.previousDates)}</span></div>
        <div className="decomposition-grid">
          <div className="effect-list">
            <div className="effect-head"><span>影响维度</span><span>变化贡献</span><span>影响金额</span></div>
            {analysis.effects.map((effect) => (
              <div className="effect-row" key={effect.key}>
                <strong>{effect.label}</strong>
                <div className="effect-track"><span className={effect.value >= 0 ? 'is-positive' : 'is-negative'} style={{ width: `${Math.max(3, Math.abs(effect.value) / maxEffect * 100)}%` }} /></div>
                <span className={effect.value >= 0 ? 'analysis-positive' : 'analysis-negative'}>{signedMoney(effect.value)}</span>
              </div>
            ))}
            <div className="effect-total"><strong>合计成交变化</strong><strong className={totalDelta >= 0 ? 'analysis-positive' : 'analysis-negative'}>{signedMoney(totalDelta)}</strong></div>
          </div>
          <div className="analysis-conclusion">
            <h3>结论</h3>
            <p>近7天成交金额较前7天<strong>{direction} {money(Math.abs(totalDelta))}</strong>，主要影响来自<strong>{mainEffect.label}（{signedMoney(mainEffect.value)}）</strong>。</p>
            <p>{mainEffect.key === 'traffic' ? '优先判断高贡献SPU的访客来源变化。' : mainEffect.key === 'conversion' ? '优先复核高访客SPU的成交承接效率。' : '优先检查规格、组合销售和高低客单结构变化。'}所有线上调整仍需Gavin确认。</p>
            <small>计算口径：商品访客 × 成交客户转化率 × 客单价；SPU汇总。</small>
          </div>
        </div>
      </section>}

      {mode === 'traffic' && <TrafficStructureSection
        snapshot={p1Snapshot}
        selectedDate={selectedDate}
        loading={analysisExpansion.loading}
        error={analysisExpansion.error}
      />}

      {mode === 'advertising' && <AdvertisingEfficiencySection
        snapshot={p2Snapshot}
        productsByKey={productsByKey}
        loading={analysisExpansion.loading}
        error={analysisExpansion.error}
      />}

      {mode === 'business' && <section className="analysis-section">
        <div className="section-heading-row"><h2>商品贡献</h2><span>按近7天成交金额变化排序</span></div>
        <div className="contribution-grid">
          <ContributionTable title="增长贡献SPU" items={analysis.growth} />
          <ContributionTable title="下降贡献SPU" items={analysis.decline} />
        </div>
      </section>}

      {mode === 'traffic' && <EfficiencyQuadrant
        snapshot={p3Snapshot}
        productsById={productsById}
        loading={analysisExpansion.loading}
        error={analysisExpansion.error}
      />}

      {mode === 'refunds' && <RefundRiskSection
        snapshot={p5Snapshot}
        productsByKey={productsByKey}
        loading={analysisExpansion.loading}
        error={analysisExpansion.error}
      />}

      {mode === 'business' && (
        <footer className="methodology-note">
          SPU汇总口径；成交金额＝商品访客 × 成交客户转化率 × 客单价，不与店铺UV混用。商品或活动线上调整仍需Gavin当次确认。
        </footer>
      )}
      {mode === 'traffic' && (
        <footer className="methodology-note">
          流量来源按独立窗口和来源层级展示；商品效率按最近7天SPU口径计算，两类访客不混加。主图、详情、投放或商品调整仍需Gavin当次确认。
        </footer>
      )}
      {mode === 'advertising' && (
        <footer className="methodology-note">
          广告诊断采用固定15天点击归因，搜索词与商品SKU报表独立分析；候选仅用于复核，否词、出价、预算或暂停均需Gavin当次确认。
        </footer>
      )}
      {mode === 'refunds' && (
        <footer className="methodology-note">
          取消退款金额包含跨期退款；退款额占比不等于退货率，也不用于反推净成交。商品、客服或售后流程调整仍需Gavin当次确认。
        </footer>
      )}
      </div>
    </div>
  )
}
